import "./pricingcalendar.css";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { toast } from "react-hot-toast";
import { useEffect, useState, useMemo } from "react";
import API from "src/API2";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcPlus     = () => <Svg><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const IcCalendar = () => <Svg><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></Svg>;
const IcClose    = () => <Svg size={18}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcArchive  = () => <Svg size={14}><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></Svg>;
const IcTag      = () => <Svg><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></Svg>;

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return "—"; }
};
const fmtDateMinusOne = (d) => {
  try {
    const dt = new Date(d);
    dt.setDate(dt.getDate() - 1);
    return dt.toLocaleDateString("fr-FR");
  } catch { return "—"; }
};

/* ── Calendar preview modal ─────────────────────────────────────── */
const CalendarModal = ({ setOpen, pricingRules }) => {
  const [events, setEvents] = useState();
  const localizer = momentLocalizer(moment);

  useEffect(() => {
    setEvents(
      (pricingRules || []).map((e) => ({
        title: e.rate.code,
        start: new Date(e.start_date),
        end: new Date(e.end_date),
      })),
    );
  }, [pricingRules]);

  return (
    <div className="pc-modal-overlay" onClick={() => setOpen(false)}>
      <div className="pc-modal pc-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="pc-modal__head">
          <div>
            <span className="pc-modal__eyebrow">Offers preview</span>
            <h3>Active and upcoming offers</h3>
          </div>
          <button className="pc-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="pc-modal__body">
          <div className="pc-rbc">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ minHeight: 520 }}
              eventPropGetter={(event, start, end, isSelected) => {
                const style = {
                  backgroundColor: "#043263",
                  color: "#fff",
                  borderRadius: "6px",
                  border: "none",
                  minHeight: "60px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  outline: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: "0.3px",
                };
                if (isSelected) style.backgroundColor = "#16a34a";
                else if (end < new Date()) {
                  style.backgroundColor = "#e5e7eb";
                  style.color = "#475569";
                } else if (start <= new Date() && new Date() <= end) {
                  style.backgroundColor = "#86efac";
                  style.color = "#064e3b";
                }
                return { style };
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Add-offer modal ────────────────────────────────────────────── */
const Modal = ({ setOpen, setPricingRules }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [publicationDate, setPublicationDate] = useState("");
  const [offers, setOffers] = useState();
  const [selectedRate, setSelectedRate] = useState("");

  useEffect(() => {
    API.get("rates/offers").then((resp) => setOffers(resp.data.offers));
  }, []);

  const post = () => {
    if (!selectedRate) { toast.error("Select an offer"); return; }
    if (!startDate || !endDate || !publicationDate) {
      toast.error("All dates are required"); return;
    }
    toast.loading("Adding new offer…");
    API.post("offerpricing", {
      start_date: new Date(startDate),
      end_date: new Date(new Date(endDate).setDate(new Date(endDate).getDate() + 1)),
      visibility_date: new Date(publicationDate),
      rate_id: selectedRate,
    })
      .then((resp) => {
        toast.dismiss();
        toast.success("Offer added");
        setPricingRules((prev) =>
          [...prev, resp.data.pricing].sort(
            (a, b) => new Date(a.start_date) - new Date(b.start_date),
          ),
        );
        setTimeout(() => setOpen(false), 180);
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error?.response?.data?.message || "Could not add offer");
      });
  };

  return (
    <div className="pc-modal-overlay" onClick={() => setOpen(false)}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <header className="pc-modal__head">
          <div>
            <span className="pc-modal__eyebrow">New offer</span>
            <h3>Schedule promotional offer</h3>
          </div>
          <button className="pc-icon-btn" onClick={() => setOpen(false)} aria-label="Close">
            <IcClose />
          </button>
        </header>
        <div className="pc-modal__body">
          <label className="pc-field">
            <span>Offer</span>
            <select
              value={selectedRate}
              onChange={(e) => setSelectedRate(e.target.value)}
            >
              <option value="" disabled>Select offer</option>
              {offers?.map((e) => (
                <option key={e.id} value={e.id}>{e.code}</option>
              ))}
            </select>
          </label>

          <div className="pc-field-row">
            <label className="pc-field">
              <span>Start date</span>
              <input
                type="date"
                max={endDate || undefined}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="pc-field">
              <span>End date</span>
              <input
                type="date"
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>

          <label className="pc-field">
            <span>Publication date</span>
            <input
              type="date"
              max={endDate || undefined}
              value={publicationDate}
              onChange={(e) => setPublicationDate(e.target.value)}
            />
          </label>
        </div>
        <footer className="pc-modal__foot">
          <button className="pc-btn pc-btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
          <button className="pc-btn pc-btn--primary" onClick={post}>Schedule offer</button>
        </footer>
      </div>
    </div>
  );
};

/* ── Main screen ────────────────────────────────────────────────── */
const OffersCalendar = () => {
  const [modalActive, setModalActive] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [pricingRules, setPricingRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get("offerpricing")
      .then((resp) => setPricingRules(resp.data.pricings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const counts = useMemo(() => {
    const now = new Date();
    let active = 0, upcoming = 0, expired = 0;
    pricingRules.forEach((r) => {
      const s = new Date(r.start_date);
      const e = new Date(r.end_date);
      if (e < now) expired += 1;
      else if (s > now) upcoming += 1;
      else active += 1;
    });
    return { active, upcoming, expired };
  }, [pricingRules]);

  const statusOf = (rule) => {
    const now = new Date();
    const s = new Date(rule.start_date);
    const e = new Date(rule.end_date);
    if (e < now) return { key: "expired", label: "Expired" };
    if (s > now) return { key: "upcoming", label: "Upcoming" };
    return { key: "active", label: "Live" };
  };

  return (
    <div className="pc-screen">
      {calendarOpen && <CalendarModal setOpen={setCalendarOpen} pricingRules={pricingRules} />}
      {modalActive && <Modal setOpen={setModalActive} setPricingRules={setPricingRules} />}

      <div className="pc-toolbar">
        <div className="pc-toolbar__left">
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Live</span>
            <span className="pc-mini-stat__value pc-mini-stat__value--green">{counts.active}</span>
          </div>
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Upcoming</span>
            <span className="pc-mini-stat__value pc-mini-stat__value--blue">{counts.upcoming}</span>
          </div>
          <div className="pc-mini-stat">
            <span className="pc-mini-stat__label">Expired</span>
            <span className="pc-mini-stat__value pc-mini-stat__value--muted">{counts.expired}</span>
          </div>
        </div>
        <div className="pc-toolbar__right">
          <button className="pc-btn pc-btn--ghost" onClick={() => setCalendarOpen(true)}>
            <IcCalendar /> Preview calendar
          </button>
          <button className="pc-btn pc-btn--primary" onClick={() => setModalActive(true)}>
            <IcPlus /> New offer
          </button>
        </div>
      </div>

      <section className="pc-card">
        <header className="pc-card__head">
          <div>
            <h3>Promotional offers</h3>
            <p>Scheduled offer windows with publication dates.</p>
          </div>
        </header>

        {loading ? (
          <div className="pc-empty">
            <IcTag /> <span>Loading offers…</span>
          </div>
        ) : pricingRules.length === 0 ? (
          <div className="pc-empty">
            <IcTag />
            <span>No offers scheduled yet.</span>
            <button className="pc-btn pc-btn--primary" onClick={() => setModalActive(true)}>
              <IcPlus /> Schedule first offer
            </button>
          </div>
        ) : (
          <div className="pc-table-wrap">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Status</th>
                  <th>Published</th>
                  <th>Start</th>
                  <th>End</th>
                  <th className="pc-table__actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pricingRules.map((rule, i) => {
                  const st = statusOf(rule);
                  return (
                    <tr key={rule.id || i}>
                      <td>
                        <span className="pc-rate-chip">{rule?.rate?.code}</span>
                      </td>
                      <td>
                        <span className={`pc-status pc-status--${st.key}`}>{st.label}</span>
                      </td>
                      <td>{fmtDate(rule.visibility_date)}</td>
                      <td>{fmtDate(rule.start_date)}</td>
                      <td>{fmtDateMinusOne(rule.end_date)}</td>
                      <td className="pc-table__actions-col">
                        <button className="pc-icon-btn" title="Archive">
                          <IcArchive />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default OffersCalendar;
